"use client";

import { useEffect, useRef, useState } from "react";
import { Button, Form, Input, message } from "antd";
import AntdStyleRegistry from "@/app/AntdRegistry";
import { HoneypotField } from "@/app/components/HoneypotField";
import { useRecaptcha } from "@/lib/recaptcha.client";
import { RECAPTCHA_ACTIONS, RENDERED_AT_FIELD, HONEYPOT_FIELD } from "@/lib/form-guard";
import { subjectForRef } from "../contact-refs";
import { CONTACT_REF_SCROLLED_EVENT, SMOOTH_SCROLL_MS } from "../contact-ref-scroll";

const { TextArea } = Input;

interface ContactFormValues {
    name: string;
    email: string;
    phone: string;
    subject: string;
    message: string;
}

export function ContactForm() {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [successfulSubmission, setSuccessfulSubmission] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();
    // Drives the subject-field animation when prefilled from a `ref` param.
    // "idle" → no ref, no animation. "pending" → ref matched, waiting for
    // scroll to finish. "filling" → value has been set, fade classes applied.
    // While not idle the subject is read-only: the ref decides it.
    const [prefillPhase, setPrefillPhase] = useState<"idle" | "pending" | "filling">("idle");
    const subjectLocked = prefillPhase !== "idle";
    // Decoy field. Anything here at submit time means a script filled it.
    const [honeypot, setHoneypot] = useState("");
    const { execute: executeRecaptcha } = useRecaptcha();
    // Stamped on mount rather than at render so it reflects when this visitor
    // actually loaded the form -- the page is statically generated, so a
    // render-time value would be build time.
    const renderedAt = useRef<number | null>(null);

    useEffect(() => {
        renderedAt.current = Date.now();
    }, []);

    // Read `?ref=...` from the URL and prefill the subject when recognized.
    // Done via window.location instead of `useSearchParams` so the page can
    // remain statically generated without an extra Suspense boundary.
    // The value is set once ContactRefScroller reports the form is in view, so
    // the fade-in happens on screen; a timer covers the page not scrolling at
    // all (a link opened in a background tab stays at the top until shown).
    useEffect(() => {
        if (typeof window === "undefined") return;
        const params = new URLSearchParams(window.location.search);
        const subject = subjectForRef(params.get("ref"));
        if (!subject) return;

        setPrefillPhase("pending");
        const fill = () => {
            form.setFieldsValue({ subject });
            setPrefillPhase("filling");
        };
        window.addEventListener(CONTACT_REF_SCROLLED_EVENT, fill, { once: true });
        const fallback = window.setTimeout(fill, SMOOTH_SCROLL_MS + 500);

        return () => {
            window.removeEventListener(CONTACT_REF_SCROLLED_EVENT, fill);
            window.clearTimeout(fallback);
        };
    }, [form]);

    const onFinish = async (values: ContactFormValues) => {
        setLoading(true);
        try {
            // Minted per submission. Resolves to null if reCAPTCHA is blocked
            // or unreachable; the server treats a missing token as a failed
            // verification, so this is not a silent bypass.
            const recaptchaToken = await executeRecaptcha(RECAPTCHA_ACTIONS.contactForm);

            const response = await fetch("/api/contact", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    ...values,
                    recaptchaToken,
                    [HONEYPOT_FIELD]: honeypot,
                    [RENDERED_AT_FIELD]: renderedAt.current,
                }),
            });

            if (!response.ok) {
                throw new Error("Failed to send message");
            }

            setSuccessfulSubmission(true);
            messageApi.success("Thank you! Your message has been sent successfully.");
            form.resetFields();
            setHoneypot("");
        } catch (error) {
            console.error("Contact form error:", error);
            messageApi.error("Sorry, there was an error sending your message. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <AntdStyleRegistry>
            {contextHolder}
            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                className="w-full flex flex-col flex-1"
            >
                <HoneypotField value={honeypot} onChange={setHoneypot} />

                <Form.Item
                    name="name"
                    label={<span className="text-gray-700 font-medium">Name</span>}
                    rules={[{ required: true, message: "Please enter your name" }]}
                >
                    <Input
                        size="large"
                        placeholder="Your full name"
                        className="rounded-md"
                    />
                </Form.Item>

                <Form.Item
                    name="email"
                    label={<span className="text-gray-700 font-medium">Email</span>}
                    rules={[
                        { required: true, message: "Please enter your email" },
                        { type: "email", message: "Please enter a valid email address" },
                    ]}
                >
                    <Input
                        size="large"
                        placeholder="your.email@example.com"
                        className="rounded-md"
                    />
                </Form.Item>

                <Form.Item
                    name="phone"
                    label={<span className="text-gray-700 font-medium">Phone</span>}
                    rules={[{ required: true, message: "Please enter your phone number" }]}
                >
                    <Input
                        size="large"
                        placeholder="(555) 123-4567"
                        className="rounded-md"
                    />
                </Form.Item>

                <Form.Item
                    name="subject"
                    label={<span className="text-gray-700 font-medium">Subject</span>}
                    rules={[{ required: true, message: "Please enter a subject" }]}
                    className={`subject-field subject-field--${prefillPhase}`}
                >
                    <Input
                        size="large"
                        placeholder="What is this regarding?"
                        className="rounded-md"
                        readOnly={subjectLocked}
                        aria-readonly={subjectLocked || undefined}
                        title={subjectLocked ? "Subject is set by the page you came from" : undefined}
                    />
                </Form.Item>
                <style>{`
                    /* Subject-field prefill animation.
                       When a recognized ref param is in the URL we first scroll
                       the form into view, then fade the value in and wash the
                       background with a soft green to signal "we've got you". */
                    .subject-field .ant-input {
                        transition: background-color 700ms ease-out, opacity 600ms ease-out;
                    }
                    .subject-field--pending .ant-input {
                        opacity: 0;
                    }
                    .subject-field--pending .ant-input,
                    .subject-field--filling .ant-input {
                        cursor: default;
                    }
                    .subject-field--filling .ant-input {
                        opacity: 1;
                        /* Soft emerald / mint — "available, ready to help" vibe. */
                        background-color: #ecfdf5;
                        animation: subject-green-wash 2200ms ease-out 1 forwards;
                    }
                    @keyframes subject-green-wash {
                        0%   { background-color: #ffffff; }
                        25%  { background-color: #d1fae5; }
                        100% { background-color: #f6fefb; }
                    }
                `}</style>

                <Form.Item
                    name="message"
                    label={<span className="text-gray-700 font-medium">Message</span>}
                    rules={[{ required: true, message: "Please enter your message" }]}
                    className="flex-1 [&_.ant-form-item-control]:flex-1 [&_.ant-form-item-control-input]:h-full [&_.ant-form-item-control-input-content]:h-full"
                >
                    <TextArea
                        placeholder="Tell us how we can help..."
                        className="rounded-md h-full! min-h-[120px]! resize-none!"
                    />
                </Form.Item>

                <Form.Item>
                    {
                        successfulSubmission ? (
                            <>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    disabled={true}
                                    className="w-full rounded-sm py-3 px-10 text-center hover:bg-linear-to-t! hover:from-[#ffffff25]! hover:to-gcharcoal! text-sm lg:text-md! bg-gcharcoal! text-gyellow! uppercase! font-semibold! tracking-wide transition-all duration-200 ease-in-out hover:shadow-lg active:scale-95 active:shadow-inner"
                                >
                                    Send Message
                                </Button>
                                <img height="1" width="1" style={{ borderStyle: 'none' }} alt="" src="https://insight.adsrvr.org/track/pxl/?adv=bh6okxy&ct=0:axvv05i&fmt=3" />                                
                            </>
                        ) : (
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                className="w-full rounded-sm py-3 px-10 text-center hover:bg-linear-to-t! hover:from-[#ffffff25]! hover:to-gcharcoal! text-sm lg:text-md! bg-gcharcoal! text-gyellow! uppercase! font-semibold! tracking-wide transition-all duration-200 ease-in-out hover:shadow-lg active:scale-95 active:shadow-inner"
                            >
                                Send Message
                            </Button>
                        )
                    }
                </Form.Item>
            </Form>
        </AntdStyleRegistry>
    );
}
